import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { GitPullRequestArrowIcon, GitPullRequestClosedIcon, GitPullRequestDraftIcon, HistoryIcon } from 'lucide-react';
import { memo, Suspense, useMemo } from 'react';

import { CopyableCommit } from '@/components/copyable-commit';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { GithubCommit, pullCommitsQuery } from '@/features/pulls/services/pulls';
import { getTimeAgo } from '@/lib/time';
import { IssueEvent, timelineEventsQuery } from '@/services/timeline';

type TimelineItem =
  | { type: 'commit'; data: GithubCommit; date: string }
  | { type: 'event'; data: IssueEvent; date: string };

const TimelineEvent = memo(({ event }: { event: IssueEvent }) => {
  switch (event.event) {
    case 'ready_for_review': {
      return (
        <div key={event.id} className="flex items-center gap-3 p-2 rounded-md">
          <div className="text-green-600 bg-green-100 rounded-sm size-7 flex items-center justify-center overflow-hidden">
            <GitPullRequestArrowIcon size={18} />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <Avatar className="size-5">
              {event.actor?.avatar_url ? (
                <AvatarImage src={event.actor.avatar_url} alt={event.actor.login || 'User'} />
              ) : (
                <AvatarFallback>{(event.actor?.login || 'UN').substring(0, 2) || '??'}</AvatarFallback>
              )}
            </Avatar>
            <span className="text-sm font-light">{event.actor?.login || 'Someone'} opened this PR</span>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{getTimeAgo(event.created_at)}</span>
        </div>
      );
    }
    case 'converted_to_draft': {
      return (
        <div key={event.id} className="flex items-center gap-3 p-2 rounded-md">
          <div className="text-gray-600 bg-gray-100 rounded-sm size-7 flex items-center justify-center overflow-hidden">
            <GitPullRequestDraftIcon size={18} />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <Avatar className="size-5">
              {event.actor?.avatar_url ? (
                <AvatarImage src={event.actor.avatar_url} alt={event.actor.login || 'User'} />
              ) : (
                <AvatarFallback>{(event.actor?.login || 'UN').substring(0, 2) || '??'}</AvatarFallback>
              )}
            </Avatar>
            <span className="text-sm font-light">{event.actor?.login || 'Someone'} converted this PR to a draft</span>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{getTimeAgo(event.created_at)}</span>
        </div>
      );
    }
    case 'merged': {
      const commitFromEvent = {
        sha: event.commit_id,
        author: {
          login: event.actor.login,
          avatar_url: event.actor.avatar_url,
          name: event.actor.name,
        },
        commit: {
          author: {
            name: event.actor.name ?? undefined,
            date: event.created_at,
          },
        },
      } as GithubCommit;
      return <CopyableCommit key={commitFromEvent.sha} commit={commitFromEvent} merged={true} />;
    }
    case 'reopened': {
      return (
        <div key={event.id} className="flex items-center gap-3 p-2 rounded-md">
          <div className="text-green-500 bg-green-100 rounded-sm size-7 flex items-center justify-center overflow-hidden">
            <GitPullRequestArrowIcon size={18} />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <Avatar className="size-5">
              {event.actor?.avatar_url ? (
                <AvatarImage src={event.actor.avatar_url} alt={event.actor.login || 'User'} />
              ) : (
                <AvatarFallback>{(event.actor?.login || 'UN').substring(0, 2) || '??'}</AvatarFallback>
              )}
            </Avatar>
            <span className="text-sm font-light">{event.actor?.login || 'Someone'} re-opened this PR</span>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{getTimeAgo(event.created_at)}</span>
        </div>
      );
    }
    case 'closed': {
      return (
        <div key={event.id} className="flex items-center gap-3 p-2 rounded-md">
          <div className="text-red-500 bg-red-100 rounded-sm size-7 flex items-center justify-center overflow-hidden">
            <GitPullRequestClosedIcon size={18} />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <Avatar className="size-5">
              {event.actor?.avatar_url ? (
                <AvatarImage src={event.actor.avatar_url} alt={event.actor.login || 'User'} />
              ) : (
                <AvatarFallback>{(event.actor?.login || 'UN').substring(0, 2) || '??'}</AvatarFallback>
              )}
            </Avatar>
            <span className="text-sm font-light">{event.actor?.login || 'Someone'} closed this PR</span>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{getTimeAgo(event.created_at)}</span>
        </div>
      );
    }
    default:
      return null;
  }
});

const PullActivityPopoverInner = () => {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  const [{ data: events }, { data: commits }] = useSuspenseQueries({
    queries: [timelineEventsQuery(owner, repo, number), pullCommitsQuery(owner, repo, number)],
  });

  const timeline = useMemo(() => {
    if (!commits || !events) return [];
    const commitItems: TimelineItem[] = commits.map((commit) => ({
      type: 'commit',
      data: commit,
      date: commit.commit?.author?.date || commit.commit?.committer?.date || '',
    }));

    const eventItems: TimelineItem[] = events.map((event) => ({
      type: 'event',
      data: event,
      date: event.created_at,
    }));

    // Combine and sort by date (newest first)
    const combined = [...commitItems, ...eventItems];
    return combined.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [commits, events]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex items-center p-2 gap-1 hover:bg-stone-200 rounded-md cursor-pointer overflow-hidden min-w-8">
          <HistoryIcon size={16} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground truncate">
            {formatDistanceToNow(timeline[0].date, { addSuffix: true })}
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-2 max-h-[80vh] overflow-y-auto" side="bottom">
        <div className="space-y-1">
          {timeline.map((item) =>
            item.type === 'commit' ? (
              <CopyableCommit key={item.data.sha} commit={item.data} />
            ) : (
              <TimelineEvent key={item.data.id} event={item.data} />
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const PullActivityPopover = memo(() => {
  return (
    <Suspense fallback={<Skeleton className="h-6 w-28" />}>
      <PullActivityPopoverInner />
    </Suspense>
  );
});
